import { APIRequestError } from "@rapid/hooks/useApi";
import Button from "@scripts/react/components/Button";
import InlineAlert from "@scripts/react/components/InlineAlert";
import React, { useEffect } from "react";
import { TooltipContainer } from "@scripts/react/components/Tooltip";
import * as Sentry from "@sentry/react";
import { twClassNames } from "@scripts/react/helpers/twClassNames";

const translateError = (error: Error | string): string => {
    if (typeof error === "string") {
        return error;
    }

    if (error instanceof APIRequestError) {
        switch (error.statusCode) {
            case 400:
                return "Bad request. Please check your input and try again.";
            case 401:
                return "You are not authorized to perform this action.";
            case 403:
                return "You do not have permission to access this resource.";
            case 404:
                return "The requested resource was not found.";
            case 500:
            case 502:
                return "An internal server error occurred. Please try again later.";
            case 503:
                return "The service is currently unavailable. Please try again later.";
            case 504:
                return "The request timed out. Please try again.";
            default:
                return `Error ${error.statusCode}: ${error.message}`;
        }
    }
    if (error instanceof TypeError) {
        return "Network error. Please check your connection and try again.";
    }
    switch (error.message) {
        case "Failed to fetch":
            return "Network error. Please check your connection and try again.";
        case "Unauthorized":
            return "You are not authorized to perform this action.";
        case "Forbidden":
            return "You do not have permission to access this resource.";
        case "Not Found":
            return "The requested resource was not found.";
        case "Internal Server Error":
            return "An internal server error occurred. Please try again later.";
        case "Service Unavailable":
            return "The service is currently unavailable. Please try again later.";
        case "Gateway Timeout":
            return "The request timed out. Please try again.";
        default:
            return error.message || "An unknown error occurred.";
    }
};

export const ErrorInfo = ({
    error,
    onRetryClick,
    type = "secondary",
    retrying = false,
    dense = false
}: {
    error: Error | string;
    onRetryClick: (e: React.MouseEvent) => void;
    type?: "primary" | "secondary";
    retrying?: boolean;
    dense?: boolean;
}) => {
    useEffect(() => {
        if (error) {
            Sentry.captureException(error);
        }
    }, [error]);

    return (
        <InlineAlert
            level="danger"
            type={type}
            icon={retrying ? "fa fa-spinner fa-spin" : "fa fa-exclamation-triangle"}
            dense={dense}
            className={twClassNames("flex items-center justify-between", !dense && "py-3 pr-3")}
            cta={
                <TooltipContainer
                    type="titleOnly"
                    title="Retry"
                    customButton={
                        <Button
                            type="red-ghost"
                            onClick={onRetryClick}
                            className="!text-destructive"
                            size="small"
                            shape="square"
                        >
                            <i className="fa-solid fa-rotate-right" />
                        </Button>
                    }
                />
            }
        >
            <span>{translateError(error)}</span>
        </InlineAlert>
    );
};
